import Input from "components/OldInput";
import {
    getCustomsTermsOptions,
    getVoecCustomsTermsOption,
} from "helpers/CustomsTerms";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components/macro";
import Context from "context/Global";
import { incoTerms } from "constants/IncoTerms";
import { currencyList } from "constants/currency";
import Api from "services/Api/Api";
import toast from "react-hot-toast";
import {
    OTHER_BUYER_CARRIERS,
    THIRD_PARTY_CARRIERS,
    VOEC_CARRIERS,
    VOEC_ELIGIBLE_PRODUCTS,
} from "views/Contacts/constants";
import InformationHover from "components/InformationHover";
import { getProductName } from "helpers/Services";
import Alert from "components/Alerts/Alert";

const CustomsTerms = () => {
    let Title = styled.div`
        font-size: 24px;
        color: rgba(0, 0, 0, 0.6);
        padding: 16px 0px;
    `;

    const context = useContext(Context);
    let hasFetchedThirdPartyContactsFromAPI = false;
    let hasFetchedOtherBuyerContactsFromAPI = false;

    const carrier = context?.quotation?.service?.name?.toUpperCase();
    const productName = getProductName(context.quotation);

    const useOtherCustomsPayeeTemplate =
        THIRD_PARTY_CARRIERS.includes(carrier) ||
        OTHER_BUYER_CARRIERS.includes(carrier);
    const useThirdPartyTemplate = THIRD_PARTY_CARRIERS.includes(carrier);
    const useOtherBuyerTemplate = OTHER_BUYER_CARRIERS.includes(carrier);

    const [isOtherCustomsPayee, setOtherCustomsPayee] = useState(false);
    const [isThirdPartyPaying, setThirdPartyPaying] = useState(false);
    const [thirdPartyOptions, setThirdPartyOptions] = useState([]);
    const [isOtherBuyer, setOtherBuyer] = useState(false);
    const [otherBuyerOptions, setOtherBuyerOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sendWithVoec, setSendWithVoec] = useState(false);

    const customsTermsOptions = getCustomsTermsOptions({
        carrier,
        product: productName,
        fromCountryCode: context?.order?.sender?.countryCode,
        toCountryCode: context?.order?.receiver?.countryCode,
    });

    // This is a very specific use case for our customer Smartoptics AS, as they always have NOK as default.
    // This should in no way be used as a general solution. This is a minimum way to solve the issue for now and
    // is ugly.
    const isDhlExpressFrejaNorway =
        context.user.customer.settings?.dhlExpressFrejaNorway;
    const staticDDPCostUPS =
        context.user.customer.settings?.staticDDPCostUPS ?? 197;

    // VOEC settings
    const { voecNumber } = context.user.customer.settings;
    const destinationCountryIsNorway =
        context.order.receiver.countryCode === "NO";
    const voecCustomsTermsOption = getVoecCustomsTermsOption(
        carrier,
        productName,
    );

    const isVoecEligibleCarrier = (carrier, productName) => {
        if (!VOEC_CARRIERS.includes(carrier.toUpperCase())) {
            return false;
        }
        // Special rule for DHL
        if (carrier.toUpperCase() === "DHL") {
            return VOEC_ELIGIBLE_PRODUCTS.includes(productName.toUpperCase());
        }

        return true;
    };
    const isPrivateReceiver = context.order.receiver.contact.private;

    const displayVoecOption =
        isVoecEligibleCarrier(carrier, productName) &&
        destinationCountryIsNorway &&
        isPrivateReceiver &&
        voecNumber;

    const [customsInformation, setCustomsInformation] = useState(() => {
        const defaultCurrency = isDhlExpressFrejaNorway ? "NOK" : "SEK";
        const defaultCustomsValue = "0";

        const availableCurrencies = currencyList.map(
            (currency) => currency.value,
        );
        const availableCustomsTerms = customsTermsOptions.map(
            (term) => term.value,
        );

        const orderCurrency = context.order?.customsInvoice?.currency;
        const orderCustomsValue =
            context.order?.customsInvoice?.customsValue?.toString();
        const orderCustomsTerms = context.order?.customsInvoice?.incoTerms;

        const isValidCurrency = availableCurrencies.includes(orderCurrency);
        const isValidCustomsTerms =
            availableCustomsTerms.includes(orderCustomsTerms);

        return {
            currency: isValidCurrency ? orderCurrency : defaultCurrency,
            customsValue:
                isValidCurrency && orderCustomsValue
                    ? orderCustomsValue
                    : defaultCustomsValue,
            customsTerms: isValidCustomsTerms
                ? orderCustomsTerms
                : customsTermsOptions[0].value,
            thirdPartyContactId: null,
        };
    });

    const fetchThirdPartyContacts = async () => {
        setIsLoading(true);
        try {
            const data = await Api.getThirdPartyContacts({
                countryCode: context.order.receiver.countryCode,
                carrier,
            });
            hasFetchedThirdPartyContactsFromAPI = true;

            const options = data.map((contact) => ({
                value: contact.id,
                title: contact.company,
            }));

            setThirdPartyOptions(options);

            if (options.length > 0) {
                setCustomsInformation((prev) => ({
                    ...prev,
                    thirdPartyContactId: options[0].value.toString(),
                }));
            }
        } catch (error) {
            toast.error(
                "Något gick fel när vi hämtade tredjepartskontakter. Försök igen.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOtherBuyerContacts = async () => {
        setIsLoading(true);
        try {
            const data = await Api.getOtherBuyerContacts({
                countryCode: context.order.receiver.countryCode,
            });
            hasFetchedOtherBuyerContactsFromAPI = true;

            const options = data.map((contact) => ({
                value: contact.id,
                title: contact.company,
            }));

            setOtherBuyerOptions(options);

            if (options.length > 0) {
                setCustomsInformation((prev) => ({
                    ...prev,
                    otherBuyerContactId: options[0].value.toString(),
                }));
            }
        } catch (error) {
            toast.error(
                "Något gick fel när vi hämtade annan köpare. Försök igen.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (
            useThirdPartyTemplate &&
            !hasFetchedThirdPartyContactsFromAPI &&
            context?.order?.receiver?.countryCode
        ) {
            fetchThirdPartyContacts();
        }
    }, []);

    useEffect(() => {
        if (
            useOtherBuyerTemplate &&
            !hasFetchedOtherBuyerContactsFromAPI &&
            context?.order?.receiver?.countryCode
        ) {
            fetchOtherBuyerContacts();
        }
    }, []);

    useEffect(() => {
        if (sendWithVoec) {
            setCustomsInformation((prev) => ({
                ...prev,
                customsTerms: voecCustomsTermsOption.value,
            }));
        }
    }, [sendWithVoec]);

    useEffect(() => {
        if (!isLoading) {
            context.updateOrder({
                information: {
                    ...context.order.information,
                    customsValue: customsInformation?.customsValue.replace(
                        ",",
                        ".",
                    ),
                    customsTerms: customsInformation?.customsTerms,
                    currency: customsInformation?.currency,
                    thirdPartyContactId: isThirdPartyPaying
                        ? customsInformation?.thirdPartyContactId
                        : null,
                    otherBuyerContactId: isOtherBuyer
                        ? customsInformation?.otherBuyerContactId
                        : null,
                    voec: {
                        isActive: sendWithVoec,
                        number: voecNumber,
                    },
                },
            });
        }
    }, [
        customsInformation?.customsValue,
        customsInformation?.customsTerms,
        customsInformation?.currency,
        customsInformation?.thirdPartyContactId,
        customsInformation?.otherBuyerContactId,
        isThirdPartyPaying,
        isOtherBuyer,
        isLoading,
    ]);

    const handleCustomsInformationChange = (updatedInfo) => {
        setCustomsInformation((prevInfo) => ({
            ...prevInfo,
            ...updatedInfo,
        }));
    };

    return (
        <div>
            <Title>Tull-villkor</Title>
            <form className="mb-2">
                <Input
                    onChange={handleCustomsInformationChange}
                    value={customsInformation}
                    type="list"
                    object={{
                        customsTerms: {
                            type: "dropdown",
                            title: "Tull-villkor",
                            required: true,
                            options: sendWithVoec
                                ? [voecCustomsTermsOption]
                                : customsTermsOptions,
                            disabled: sendWithVoec,
                        },
                    }}
                />
            </form>
            {customsTermsOptions.length === 1 && (
                <Alert type="warning">
                    Bokning med {carrier} {productName} är endast tillgänglig
                    med {customsTermsOptions[0].value}.
                </Alert>
            )}
            {displayVoecOption && (
                <form className="flex items-center mb-2 mt-4">
                    <label className="flex items-center space-x-2 text-sm">
                        <input
                            type="checkbox"
                            checked={sendWithVoec}
                            onChange={(e) => setSendWithVoec(e.target.checked)}
                            className="h-5 w-5"
                            style={{ accentColor: "rgba(76, 80, 175, 1)" }}
                        />
                        <span>Skicka med VOEC</span>
                    </label>
                </form>
            )}
            {sendWithVoec && (
                <span className="text-xs">VOEC-nummer: {voecNumber}</span>
            )}
            {useOtherCustomsPayeeTemplate && (
                <div className="pl-1 mb-2">
                    <div className="flex flex-row items-center gap-2">
                        <p
                            style={{
                                fontSize: "0.8rem",
                                fontWeight: "bold",
                            }}
                        >
                            Är betalare av tull och moms någon annan än
                            avsändare eller mottagare?
                        </p>
                        <InformationHover>
                            För att använda denna funktion, krävs det att det
                            finns en registrerad tredjepart eller annan köpare i
                            adressboken. Denne ska vara i samma land som
                            mottagaren.
                        </InformationHover>
                    </div>
                    <div className="flex flex-col">
                        <label>
                            <input
                                type="radio"
                                name="customsOptions"
                                className="mr-2"
                                checked={!isOtherCustomsPayee}
                                onChange={() => setOtherCustomsPayee(false)}
                            />
                            Nej
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="customsOptions"
                                className="mr-2"
                                checked={isOtherCustomsPayee}
                                onChange={() => setOtherCustomsPayee(true)}
                                disabled={
                                    thirdPartyOptions.length === 0 &&
                                    otherBuyerOptions.length === 0
                                }
                            />
                            Ja
                        </label>
                    </div>
                </div>
            )}
            {isOtherCustomsPayee && (
                <div className="pl-1 mb-2">
                    <div className="flex flex-row items-center gap-2">
                        <p
                            style={{
                                fontSize: "0.8rem",
                                fontWeight: "bold",
                            }}
                        >
                            Välj typ av betalare för moms och tull
                        </p>
                        <InformationHover>
                            Är kontakten av typen tredjepart eller annan köpare?
                        </InformationHover>
                    </div>
                    <div className="flex flex-col">
                        <label>
                            <input
                                type="radio"
                                name="payeeOptions"
                                className="mr-2"
                                checked={isThirdPartyPaying}
                                onChange={() => {
                                    setOtherBuyer(false);
                                    setThirdPartyPaying(true);
                                }}
                            />
                            Tredje part
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="payeeOptions"
                                className="mr-2"
                                checked={isOtherBuyer}
                                onChange={() => {
                                    setOtherBuyer(true);
                                    setThirdPartyPaying(false);
                                }}
                            />
                            Annan köpare
                        </label>
                    </div>
                </div>
            )}
            <form>
                <Input
                    onChange={handleCustomsInformationChange}
                    value={customsInformation}
                    type="list"
                    object={{
                        thirdPartyContactId: {
                            type: "dropdown",
                            title: "Tredjepartsbetalare av tull och moms",
                            required: true,
                            options: thirdPartyOptions,
                            hidden: !isThirdPartyPaying,
                            disabled: thirdPartyOptions.length === 0,
                        },
                        otherBuyerContactId: {
                            type: "dropdown",
                            title: "Annan köpare än mottagare som betalar tull och moms",
                            required: true,
                            options: otherBuyerOptions,
                            hidden: !isOtherBuyer,
                            disabled: otherBuyerOptions.length === 0,
                        },
                        currency: {
                            type: "dropdown",
                            title: "Valuta för tullvärde",
                            required: true,
                            options: currencyList,
                        },
                        customsValue: {
                            type: "text",
                            title: "Tullvärde",
                            required: true,
                            placeholder: "",
                            maxLength: 35,
                            unit: customsInformation?.currency || "SEK",
                        },
                    }}
                />
            </form>
            {customsInformation?.customsTerms === incoTerms.ddp.code && (
                <p className="text-sm text-left mr-36 pt-3">
                    Tull och moms-kostnader faktureras till avsändaren. OBS! Med
                    DHL Freight går alltid tull och moms till mottagaren. Hos
                    UPS kostar det {staticDDPCostUPS} SEK extra pga
                    administrativ avgift.
                </p>
            )}
        </div>
    );
};

export default CustomsTerms;
